import { Link } from 'react-router-dom';
import { Head } from 'vite-react-ssg';
import FooterSection from '../../components/FooterSection';
import { WA_NUMBER } from '../../constant/detail';

const relatedServices = [
  { label: 'Jasa Maintenance Kolam Renang Bali', to: '/jasa-maintenance-kolam-renang-bali' },
  { label: 'Jual Chemical Pool Bali', to: '/jual-chemical-pool-bali' },
  { label: 'Jasa Testkit Air Kolam Bali', to: '/jasa-testkit-air-kolam-bali' },
  { label: 'Jual Equipment Pool Bali', to: '/equipment-pool-bali' },
  { label: 'Konsultasi Kolam Renang Bali', to: '/konsultasi-kolam-renang-bali' },
];

const WA_MESSAGE = encodeURIComponent(
  'Hello, I would like to inquire about pool filter installation in Bali'
);

const PasangFilterKolam = () => {
  return (
    <>
      <Head>
        <title>Pasang Filter Kolam Renang Bali | Hayward &amp; Waterco | SJT</title>
        <meta
          name="description"
          content="Jasa pasang filter kolam renang Bali profesional. Sand filter, cartridge, pompa sirkulasi. Garansi kerja, harga transparan. Melayani seluruh Bali. Hubungi kami!"
        />
        <link rel="canonical" href="https://poolshopsuryajayateknik.com/pasang-filter-kolam-renang-bali" />
        <meta property="og:title" content="Pasang Filter Kolam Renang Bali | Hayward & Waterco | SJT" />
        <meta property="og:description" content="Jasa pasang filter kolam renang Bali profesional. Sand filter, cartridge, pompa sirkulasi. Garansi kerja, harga transparan. Melayani seluruh Bali." />
        <meta property="og:image" content="https://poolshopsuryajayateknik.com/img/blog/img-2.jpg" />
        <meta property="og:url" content="https://poolshopsuryajayateknik.com/pasang-filter-kolam-renang-bali" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Pasang Filter Kolam Renang Bali | Hayward & Waterco | SJT" />
        <meta name="twitter:description" content="Jasa pasang filter kolam renang Bali profesional. Sand filter, cartridge, pompa sirkulasi. Garansi kerja, harga transparan. Melayani seluruh Bali." />
      </Head>

      <main className="mt-16 bg-white text-gray-800">
        {/* Hero */}
        <section className="bg-tertiary text-white py-16 px-6 text-center">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-3xl md:text-4xl font-bold leading-tight mb-4">
              Pool Filter Installation Bali – Professional &amp; Guaranteed
            </h1>
            <p className="text-lg text-primary/80 mb-8">
              The filtration system is the heart of a healthy and clear swimming
              pool. Without a properly functioning filter, your pool water will
              quickly turn cloudy, grow algae, and become unusable. Suryajaya
              Teknik provides pool filter installation services in Bali with
              certified technicians, complete equipment, and a work guarantee
              for your peace of mind. We handle all types of filters from sand
              filters and cartridge filters to DE filters for all pool sizes.
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:opacity-90 text-tertiary font-semibold py-3 px-8 rounded-full text-lg transition"
            >
              Consult Filter Installation
            </a>
          </div>
        </section>

        {/* Why */}
        <section className="max-w-4xl mx-auto py-14 px-6">
          <h2 className="text-2xl font-bold text-tertiary mb-4">
            Signs Your Pool Filter System Has a Problem
          </h2>
          <p className="mb-4 leading-relaxed">
            Cloudy pool water even after adding chlorine is the main sign that
            your filtration system is not working properly. A clogged or broken
            filter cannot effectively remove dirt particles, dust, and
            microorganisms from the pool water. Weak water flow from the return
            jets or noisy sounds from the pump also indicate problems with the
            circulation and filtration system. If left too long, small filter
            damage can grow into major damage requiring full unit replacement.
            By inspecting and maintaining the filter regularly, you can avoid
            far greater repair costs down the line.
          </p>
          <p className="leading-relaxed">
            Suryajaya Teknik is here to help you with new filter installation or
            replacement of old inefficient filters. Before installation, our
            team conducts a survey and needs analysis to recommend the most
            suitable filter type and capacity for your pool volume and usage. We
            work with leading filter brands that have proven quality and
            reliability in the international market. The installation is carried
            out neatly, cleanly, and according to technical standards without
            damaging the aesthetics of your pool area. After installation, we
            provide complete operational guidance and a recommended maintenance
            schedule.
          </p>
        </section>

        {/* Filter Types */}
        <section className="bg-primary/10 py-14 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-tertiary mb-6">
              Types of Pool Filters We Install
            </h2>
            <div className="grid md:grid-cols-3 gap-5">
              {[
                {
                  name: 'Sand Filter',
                  desc: 'The most common choice for medium to large pools. Silica sand media filters particles of 20-100 microns effectively. Easy to maintain with periodic backwashing.',
                },
                {
                  name: 'Cartridge Filter',
                  desc: 'Ideal for small to medium pools. Filters very fine particles down to 10 microns. Water-saving as it does not require backwashing - just clean the cartridge regularly.',
                },
                {
                  name: 'DE Filter (Diatomaceous Earth)',
                  desc: 'The most effective filter, capable of filtering particles down to 3-5 microns. Produces the clearest pool water. Best for premium pools, hotels, and resorts.',
                },
              ].map((filter, i) => (
                <div
                  key={i}
                  className="bg-white border border-primary rounded-xl p-5 shadow-sm"
                >
                  <h3 className="font-semibold text-tertiary mb-3">
                    {filter.name}
                  </h3>
                  <p className="text-gray-600 text-sm">{filter.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Services */}
        <section className="max-w-4xl mx-auto py-14 px-6">
          <h2 className="text-2xl font-bold text-tertiary mb-4">
            Installation &amp; Filter Service
          </h2>
          <ul className="space-y-3">
            {[
              'Consultation and survey for filtration system needs',
              'New filter installation (sand filter, cartridge filter, DE filter)',
              'Replacement of old filters with newer, more efficient units',
              'Circulation pump and return jet pipe installation',
              'Skimmer and main drain installation for optimal circulation',
              'Periodic backwash and filter media cleaning',
              'Replacement of worn silica sand media or cartridges',
              'Pipe leakage and filtration valve repairs',
              'Automated pump control system installation',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="text-tertiary font-bold mt-0.5">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* Why Us */}
        <section className="bg-gray-50 py-14 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-tertiary mb-6">
              Why Choose Our Service?
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Certified Technicians',
                  desc: 'All our technicians are certified and experienced in pool filtration system installation.',
                },
                {
                  title: 'Trusted Brands',
                  desc: 'We use filter products from leading brands with official warranty from distributors.',
                },
                {
                  title: 'Installation Guarantee',
                  desc: 'Every installation comes with a minimum 6-month installation guarantee for your peace of mind.',
                },
                {
                  title: 'Transparent Pricing',
                  desc: 'Survey and cost estimation done upfront with no unexpected additional charges when work is complete.',
                },
                {
                  title: 'Neat Workmanship',
                  desc: 'We ensure the work area stays clean and tidy during and after the installation process.',
                },
                {
                  title: 'After-Sales Support',
                  desc: 'We are available for technical consultation and after-sales visits within the warranty period at no extra cost.',
                },
              ].map((item, i) => (
                <div
                  key={i}
                  className="bg-white border border-primary rounded-xl p-5 shadow-sm"
                >
                  <h3 className="font-semibold text-tertiary mb-2">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-tertiary text-white py-16 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">
              Need Pool Filter Installation in Bali?
            </h2>
            <p className="text-primary/80 mb-8">
              Contact us now for a survey and cost estimate for your pool filter
              installation. Our expert team is ready to provide the best
              solution to keep your pool water clear, clean, and healthy at all
              times. Do not delay - a good filtration system is a long-term
              investment for your pool.
            </p>
            <a
              href={`https://wa.me/${WA_NUMBER}?text=${WA_MESSAGE}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-primary hover:opacity-90 text-tertiary font-semibold py-3 px-10 rounded-full text-lg transition"
            >
              Contact Us via WhatsApp
            </a>
          </div>
        </section>

        {/* Related Services */}
        <section className="max-w-4xl mx-auto py-10 px-6">
          <h2 className="text-lg font-bold text-tertiary mb-4">Layanan Lainnya</h2>
          <div className="flex flex-wrap gap-3">
            {relatedServices.map(({ label, to }) => (
              <Link key={to} to={to} className="text-sm font-medium text-primary border border-primary/40 px-4 py-2 rounded-full hover:bg-primary/10 transition">
                {label}
              </Link>
            ))}
          </div>
        </section>

        <FooterSection />
      </main>
    </>
  );
};

export default PasangFilterKolam;
